import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { PageCard } from '../../../Compounds/CardWithTitle';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { McpAccessProfile, McpAccessProfileWithToken } from '../../../../Api/McpAccess';
import { McpAccessProfilesTable } from '../../../Compounds/McpAccess/McpAccessProfilesTable';
import { McpAccessCreateForm, McpAccessCreateFormValues } from '../../../Compounds/McpAccess/McpAccessCreateForm';
import { McpAccessTokenRevealModal } from '../../../Compounds/McpAccess/McpAccessTokenRevealModal';
import { McpAccessRevokeConfirmDialog } from '../../../Compounds/McpAccess/McpAccessRevokeConfirmDialog';
import { McpAccessAuditDrawer } from '../../../Compounds/McpAccess/McpAccessAuditDrawer';
import { useMcpAccess } from '../../../../store/McpAccess';
import { buildClaudeMcpAddCommand } from '../../../Compounds/McpAccess/buildClaudeMcpAddCommand';
import { deriveMcpEnvLabel } from '../../../Compounds/McpAccess/mcpEnvLabel';
import { extractApiErrorMessage } from '../../../Compounds/McpAccess/extractApiErrorMessage';

interface RevealState {
  profile: McpAccessProfileWithToken;
  setupCommand: string;
}

export const McpAccessTab = () => {
  const { setSnack } = useSnackbar();
  const {
    profiles,
    loading,
    listError,
    submittingCreate,
    submittingRotateId,
    submittingRevokeId,
    loadProfiles,
    createProfile,
    rotateProfile,
    revokeProfile
  } = useMcpAccess();
  const [revealState, setRevealState] = useState<RevealState | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<McpAccessProfile | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const detailId = searchParams.get('detailId');
  const auditProfile = profiles.find((profile) => profile.access_id === detailId) ?? null;

  const envLabel = useMemo(() => deriveMcpEnvLabel(window.location.hostname), []);
  const serverUrl = process.env.REACT_APP_API_SERVER_URL || '';

  const openReveal = (profile: McpAccessProfileWithToken) => {
    const setupCommand = buildClaudeMcpAddCommand({
      env: envLabel,
      serverUrl,
      accessId: profile.access_id,
      token: profile.token
    });
    setRevealState({ profile, setupCommand });
  };

  const handleCreate = async (values: McpAccessCreateFormValues) => {
    const created = await createProfile({ access_profile: values });
    if (!created) {
      setSnack({
        message: 'Could not create MCP access profile',
        type: 'error',
        open: true
      });
      return;
    }
    openReveal(created);
    setSnack({ message: 'MCP access profile created', type: 'success', open: true });
  };

  const handleRotate = async (profile: McpAccessProfile) => {
    const rotated = await rotateProfile(profile.access_id);
    if (!rotated) {
      setSnack({ message: `Could not rotate ${profile.title}`, type: 'error', open: true });
      return;
    }
    openReveal(rotated);
    setSnack({ message: 'MCP access token rotated', type: 'success', open: true });
  };

  const handleRevokeRequest = (profile: McpAccessProfile) => {
    setRevokeTarget(profile);
  };

  const handleCloseAudit = () => {
    searchParams.delete('detailId');
    setSearchParams(searchParams, { replace: true });
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    const ok = await revokeProfile(revokeTarget.access_id);
    if (!ok) {
      setSnack({ message: `Could not revoke ${revokeTarget.title}`, type: 'error', open: true });
      return;
    }
    setSnack({ message: `Revoked ${revokeTarget.title}`, type: 'success', open: true });
    setRevokeTarget(null);
  };

  const handleCopyFailed = (message: string) => {
    setSnack({ message: `Copy failed: ${message}`, type: 'error', open: true });
  };

  const handleCopied = (label: string) => {
    setSnack({ message: `${label.charAt(0).toUpperCase()}${label.slice(1)} copied`, type: 'success', open: true });
  };

  return (
    <PageCard
      title="MCP Access"
      dataCy="mcp-access-tab"
      enableCollapse={false}
      childrenWrapperClass="min-h-80 max-w-[90vw] min-w-full">
      <Stack spacing={2}>
        <Typography
          variant="body2"
          color="text.secondary">
          Issue, rotate, and revoke MCP access tokens for your account. Tokens are shown exactly once at creation or
          rotation — copy them immediately.
        </Typography>

        <McpAccessCreateForm
          submitting={submittingCreate}
          onSubmit={handleCreate}
        />

        {listError && (
          <Alert
            severity="error"
            data-testid="mcp-access-list-error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={loadProfiles}
                data-testid="mcp-access-list-retry">
                Retry
              </Button>
            }>
            {extractApiErrorMessage({ message: listError }, listError)}
          </Alert>
        )}

        <Box>
          <McpAccessProfilesTable
            profiles={profiles}
            loading={loading}
            submittingRotateId={submittingRotateId}
            submittingRevokeId={submittingRevokeId}
            onRotate={handleRotate}
            onRevoke={handleRevokeRequest}
          />
        </Box>
      </Stack>

      <McpAccessTokenRevealModal
        open={revealState !== null}
        profile={revealState?.profile || null}
        setupCommand={revealState?.setupCommand || ''}
        onClose={() => setRevealState(null)}
        onCopied={handleCopied}
        onCopyFailed={handleCopyFailed}
      />

      <McpAccessRevokeConfirmDialog
        open={revokeTarget !== null}
        profile={revokeTarget}
        submitting={submittingRevokeId === revokeTarget?.access_id}
        onConfirm={handleRevokeConfirm}
        onClose={() => setRevokeTarget(null)}
      />

      <McpAccessAuditDrawer
        open={auditProfile !== null}
        profile={auditProfile}
        onClose={handleCloseAudit}
      />
    </PageCard>
  );
};
